import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useLang, useContent, loc, UI } from "@/lib/content";
import { api } from "@/lib/api";

export default function LeadForm() {
  const { content } = useContent();
  const { lang } = useLang();
  const [form, setForm] = useState({ nombre: "", email: "", telefono: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api("/leads", { method: "POST", body: form });
      setSent(true);
      toast.success(loc(UI.lead.successTitle, lang));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  const waHref = content.whatsapp_number
    ? `https://wa.me/${content.whatsapp_number.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(
        lang === "es" ? "Hola, quiero información sobre el curso de ciudadanía Cívica" : "Hi, I'd like information about the Cívica citizenship course"
      )}`
    : "";

  return (
    <section id="inscripcion" className="bg-paperdeep py-24">
      <div className="mx-auto grid max-w-5xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
        >
          <p className="flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-heritage">
            <span className="h-px w-8 bg-heritage" /> {loc(UI.lead.overline, lang)}
          </p>
          <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight text-navy sm:text-5xl">
            {loc(UI.lead.title, lang)}
            <span className="italic text-heritage">{loc(UI.lead.titleAccent, lang)}</span>
          </h2>
          <p className="mt-4 max-w-md text-inkbody">{loc(UI.lead.intro, lang)}</p>
          <ul className="mt-6 space-y-2.5 text-sm font-semibold text-navy">
            {UI.lead.bullets.map((t) => (
              <li key={t.es} className="flex items-center gap-2.5">
                <CheckCircle2 size={16} className="text-emeraldsucc" /> {loc(t, lang)}
              </li>
            ))}
          </ul>
          {waHref && (
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-cta"
              className="mt-8 inline-flex items-center gap-2.5 rounded-full border border-emeraldsucc/40 bg-emeraldsucc/10 px-6 py-3.5 text-sm font-bold text-emeraldsucc transition-all hover:-translate-y-0.5 hover:bg-emeraldsucc hover:text-paper"
            >
              <MessageCircle size={17} /> {loc(UI.lead.whatsapp, lang)}
            </a>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="rounded-3xl border border-line bg-white p-7 shadow-xl sm:p-9"
        >
          {sent ? (
            <div className="py-10 text-center" data-testid="lead-form-success">
              <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emeraldsucc/10 text-emeraldsucc">
                <CheckCircle2 size={32} />
              </span>
              <p className="mt-5 font-display text-3xl font-semibold text-navy">
                {loc(UI.lead.successTitle, lang)} {form.nombre.split(" ")[0]}
              </p>
              <p className="mt-2 text-sm text-inkbody">{loc(UI.lead.successBody, lang)}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="lead-form">
              <div>
                <label htmlFor="lf-nombre" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-mutedtext">{loc(UI.lead.name, lang)}</label>
                <input
                  id="lf-nombre"
                  data-testid="lead-input-nombre"
                  className="field"
                  required
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  placeholder={loc(UI.lead.namePh, lang)}
                />
              </div>
              <div>
                <label htmlFor="lf-email" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-mutedtext">{loc(UI.lead.email, lang)}</label>
                <input
                  id="lf-email"
                  type="email"
                  data-testid="lead-input-email"
                  className="field"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder={loc(UI.lead.emailPh, lang)}
                />
              </div>
              <div>
                <label htmlFor="lf-telefono" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-mutedtext">{loc(UI.lead.phone, lang)}</label>
                <input
                  id="lf-telefono"
                  type="tel"
                  data-testid="lead-input-telefono"
                  className="field"
                  required
                  value={form.telefono}
                  onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                  placeholder={loc(UI.lead.phonePh, lang)}
                />
              </div>
              <button
                type="submit"
                data-testid="lead-form-submit"
                disabled={sending}
                className="w-full rounded-full bg-heritage px-8 py-4 text-sm font-bold text-paper shadow-lg shadow-heritage/25 transition-all hover:-translate-y-0.5 hover:bg-heritagedark disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? loc(UI.lead.sending, lang) : loc(UI.lead.submit, lang)}
              </button>
              <p className="text-center text-[11px] leading-relaxed text-mutedtext">{loc(UI.lead.consent, lang)}</p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
